// DESCRIPTION: Booking entry screen — event hero card with title, meta, description,
// DESCRIPTION: price, and the "Book now" call to action that opens the booking wizard.

import React from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { eventTypeLabel, formatMoney, stripHtml } from './helpers';

/**
 * EventHero — the public event detail / booking entry surface.
 *
 * @param {Object}   props
 * @param {Object}   props.event        The event object from the REST API.
 * @param {Object}   props.availability Availability result (may include available_spots).
 * @param {boolean}  props.slotBased    Whether the event uses date/slot selection.
 * @param {Function} props.onBook       Called when the visitor clicks "Book now".
 * @param {Object}   [props.style]      Inline CSS custom properties (--nb-* overrides
 *                                      from getEventStyleVars()). Applied on the hero
 *                                      root so per-event tokens beat the base palette
 *                                      redeclared in event-hero.css.
 *
 * @return {JSX.Element} The hero entry card.
 */
const EventHero = ( {
	event,
	availability,
	slotBased,
	onBook,
	style = {},
} ) => {
	const price = event.price || 0;
	const symbol = event.currency_symbol || event.currency || '';
	const duration = event.duration ? event.duration : 0;
	const description = stripHtml( event.description );
	const sub =
		description.length > 120
			? `${ description.slice( 0, 120 ) }…`
			: description;

	const spots =
		availability && typeof availability.available_spots === 'number'
			? availability.available_spots
			: null;

	let typeLine = eventTypeLabel( event.event_type );
	if ( duration ) {
		typeLine = sprintf(
			/* translators: 1: event type label, 2: duration in minutes */
			__( '%1$s · %2$d min', 'nemtly-booking' ),
			eventTypeLabel( event.event_type ),
			duration
		);
	}

	// Featured image becomes the header background (via the --nb-banner CSS var +
	// the .nb-event-hero__top--has-banner modifier in event-hero.css). When absent,
	// the solid accent gradient from the base rule applies.
	const banner = event.featured_image || '';
	const topClassName = banner
		? 'nb-event-hero__top nb-event-hero__top--has-banner'
		: 'nb-event-hero__top';
	const topStyle = banner
		? { '--nb-banner': `url("${ banner }")` }
		: undefined;

	return (
		<div className="nb-event-hero" style={ style }>
			<div className={ topClassName } style={ topStyle }>
				<span className="nb-event-hero__tag">{ typeLine }</span>
				<h2 className="nb-event-hero__title">
					{ event.title?.rendered || event.title }
				</h2>
				{ sub && <p className="nb-event-hero__sub">{ sub }</p> }
			</div>
			<div className="nb-event-hero__body">
				<div className="nb-event-hero__meta">
					{ ! slotBased && (
						<div className="nb-event-hero__meta-item">
							<span
								className="nb-event-hero__meta-icon"
								aria-hidden="true"
							>
								📅
							</span>
							{ event.date ||
								__( 'Date to be announced', 'nemtly-booking' ) }
						</div>
					) }
					{ ! slotBased && event.time && (
						<div className="nb-event-hero__meta-item">
							<span
								className="nb-event-hero__meta-icon"
								aria-hidden="true"
							>
								🕙
							</span>
							{ event.time }
						</div>
					) }
					{ event.location && (
						<div className="nb-event-hero__meta-item">
							<span
								className="nb-event-hero__meta-icon"
								aria-hidden="true"
							>
								📍
							</span>
							{ event.location }
						</div>
					) }
					{ ! slotBased && spots !== null && spots > 0 && (
						<div className="nb-event-hero__meta-item">
							<span
								className="nb-event-hero__meta-icon"
								aria-hidden="true"
							>
								👥
							</span>
							{ sprintf(
								/* translators: %d: number of remaining spots */
								__( '%d spots remaining', 'nemtly-booking' ),
								spots
							) }
						</div>
					) }
				</div>

				{ description && (
					<p className="nb-event-hero__desc">{ description }</p>
				) }

				<div className="nb-event-hero__cta">
					<div>
						{ price > 0 ? (
							<div className="nb-event-hero__price">
								{ formatMoney( symbol, price ) }{ ' ' }
								<span className="nb-event-hero__price-note">
									{ __( 'per person', 'nemtly-booking' ) }
								</span>
							</div>
						) : (
							<div className="nb-event-hero__price">
								{ __( 'Free', 'nemtly-booking' ) }
							</div>
						) }
					</div>
					<button
						type="button"
						className="nemtly-button-primary nemtly-button-lg"
						onClick={ onBook }
					>
						{ __( 'Book now →', 'nemtly-booking' ) }
					</button>
				</div>
			</div>
		</div>
	);
};

export default EventHero;
